/* rt-tire-levy.js — auto-add the provincial tire levy to a self-serve storefront cart.
 *
 * WHY: every levy R&T has ever charged was added by the quote/draft path (the helper's
 * ensure-levy + the send guard). A customer who builds their own cart on rtsales.ca and
 * checks out (Shop Pay, #18857) never touches that path and pays no levy. This closes
 * that hole at the only place the storefront lets us act — the cart. Shopify checkout
 * cannot add line items on a non-Plus plan, and the shipping province is not known until
 * checkout, so this always adds the SK levy and a nightly audit tags any order whose
 * shipping province turned out to be something else.
 *
 * CONTRACT (mirrors quote-builder-app/server.py levy_items_for / is_tire_title, and the
 * Cart Transform in ~/rt-levy-function — the two MUST agree, because whichever adds a levy
 * first owns the whole cart):
 *   - a NEW tire line carries one levy unit per tire;
 *   - a wheel pack (product type Wheels) carries 4 per pack — 6 on a `Dually` pack;
 *   - used tires and takeoffs NEVER carry a levy — they paid it when sold new. Takeoff =
 *     the title word OR the product's own tag. On a PACK those marks usually mean the rims,
 *     so a pack tagged `New Tires` is levied anyway (RT 2026-09-11);
 *   - medium/commercial truck tires (R17.5/19.5/22.5/24.5) carry the higher medium levy;
 *   - a buyer tagged `LevyExempt` carries NONE — they hold their own tire-vendor licence
 *     and remit it themselves.
 *
 * SAFETY: this only ever manages the line it added itself (marked with the hidden
 * `_rt_levy` property). A levy line that arrived some other way — a staff-built cart
 * link, the customer adding it by hand — is counted toward the total but never changed
 * or removed. So the worst case is that we add nothing, never that we delete somebody's
 * line. See memory: no-levy-on-takeoffs-used, tire-levy-send-guard.
 */
(function () {
  'use strict';

  var LEVY = {
    regular: { id: 45040512729262, title: 'SK Tire Levy' },
    medium:  { id: 45229336690862, title: 'SK Tire Levy Medium Truck' }
  };
  var LEVY_IDS = [LEVY.regular.id, LEVY.medium.id];
  var MARK = '_rt_levy';          // hidden line property on the line we manage
  var PACK_TIRES = 4;
  var DUALLY_TIRES = 6;           // a `Dually` pack ships six tires on every variant
  var TAG_TTL_MS = 10 * 60 * 1000;
  var TAG_TIMEOUT_MS = 4000;

  // --- classification, kept in step with the helper's regexes ------------------------
  var USED_TAKEOFF_RE = /take[\s-]?offs?|\bused\b/i;
  var MEDIUM_TRUCK_RE = /[R/]\s?(?:17|19|22|24)\.5(?!\d)/i;
  var WHEEL_PACK_RE   = /\btires?\b[\s\S]*\b(?:rims?|wheels?)\b/i;
  var PACK_RIM_DIA_RE = /\d{2}(?:\.5)?\s*(?:["”″]|\s?inch\b|\s?in\b)|\b\d{2}\s?[xX×]\s?\d{1,2}(?:\.\d+)?\b/;
  var PACK_SERVICE_RE = /mount|balanc|install|remov|repair|swap|rotat|includ|service|labou?r|levy/i;

  function titleOf(line) {
    return [line.product_title, line.variant_title, line.title]
      .filter(Boolean).join(' ');
  }

  function isLevyLine(line) {
    return LEVY_IDS.indexOf(line.variant_id || line.id) !== -1;
  }

  function isOurs(line) {
    var p = line.properties || {};
    return !!p[MARK];
  }

  /* A buyer who holds their own provincial tire-vendor licence and remits the levy
     themselves. The customer is tagged `LevyExempt`; layout/theme.liquid resolves that in
     Liquid and hands it down as window.RT_LEVY_EXEMPT, because a customer's tags are not
     readable from /cart.js — it has to arrive with the page.

     The SAME tag the cart-transform function and the helper's levy_gate() read. All three
     have to agree, or one of them puts back what another stood down for. Strict `=== true`
     so an absent or malformed flag levies exactly as before — every anonymous cart included. */
  function buyerRemitsOwnLevy() {
    return window.RT_LEVY_EXEMPT === true;
  }

  function isWheelPack(t) {
    if (USED_TAKEOFF_RE.test(t) || PACK_SERVICE_RE.test(t)) return false;
    return WHEEL_PACK_RE.test(t) && PACK_RIM_DIA_RE.test(t);
  }

  /* A product's TAGS. /cart.js does not carry them, so read /products/<handle>.js once per
     product (kept 10 minutes in sessionStorage). null = could not read, and every rule below
     then falls back to the title alone — exactly what this script did before it read tags. */
  var tagMemo = {};
  function tagsFor(handle) {
    if (!handle) return Promise.resolve(null);
    if (tagMemo[handle]) return tagMemo[handle];
    var key = 'rt_levy_tags:' + handle;
    try {
      var hit = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (hit && Array.isArray(hit.tags) && (Date.now() - hit.at) < TAG_TTL_MS) {
        return (tagMemo[handle] = Promise.resolve(hit.tags));
      }
    } catch (e) {}
    var read = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
      .then(function (p) {
        var tags = p && Array.isArray(p.tags) ? p.tags : null;
        if (tags) {
          try { window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), tags: tags })); } catch (e) {}
        }
        return tags;
      });
    var slow = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, TAG_TIMEOUT_MS); });
    tagMemo[handle] = Promise.race([read, slow]).catch(function () { return null; });
    return tagMemo[handle];
  }

  function hasTag(tags, name) {
    name = name.toLowerCase();
    return !!tags && tags.some(function (t) { return String(t).trim().toLowerCase() === name; });
  }

  function hasTakeoffTag(tags) {
    return !!tags && tags.some(function (t) { return USED_TAKEOFF_RE.test(String(t)); });
  }

  /* How many levy units this cart line owes, and of which kind. `tags` is the product's
     tag list, or null when it could not be read.
     Returns {regular: n, medium: n}. */
  function unitsFor(line, tags) {
    var none = { regular: 0, medium: 0 };
    if (isLevyLine(line)) return none;

    var t = titleOf(line);
    var type = line.product_type || '';
    var qty = line.quantity || 0;
    // takeoffs/used already paid it — the title word, or the product's own tag
    var takeoff = USED_TAKEOFF_RE.test(t) || hasTakeoffTag(tags);
    var units = 0;
    if (type === 'Tires') {
      if (takeoff) return none;
      units = qty;
    } else if (type === 'Wheels' || (!type && isWheelPack(t))) {
      // A PACK, by product type: its title can say "K03 on … Rims" with no tire word at all.
      // "Takeoffs" on a pack often names the OEM RIMS while the tires are new; `New Tires`
      // carries that fact. Without it a takeoff-marked pack stays unlevied, as before.
      if (takeoff && !hasTag(tags, 'New Tires')) return none;
      units = qty * (hasTag(tags, 'Dually') ? DUALLY_TIRES : PACK_TIRES);
    }
    if (!units) return none;

    return MEDIUM_TRUCK_RE.test(t) ? { regular: 0, medium: units }
                                   : { regular: units, medium: 0 };
  }

  // --- cart plumbing -----------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  /* Work out the one change (if any) this cart needs for one levy kind. `tags` maps a
     product handle to its tags (or null when unreadable). */
  function planFor(cart, kind, tags) {
    // For an exempt buyer `want` stays 0, which walks OUR OWN line back to zero through the
    // wind-back path below. That is deliberate: it corrects a cart they filled BEFORE
    // logging in, and it still never touches a levy line somebody else put there.
    var want = 0;
    if (!buyerRemitsOwnLevy()) {
      cart.items.forEach(function (l) { want += unitsFor(l, (tags || {})[l.handle] || null)[kind]; });
    }

    var variantId = LEVY[kind].id;
    var have = 0, ours = null;
    cart.items.forEach(function (l) {
      if ((l.variant_id || l.id) !== variantId) return;
      have += l.quantity || 0;
      if (isOurs(l)) ours = l;
    });

    if (have === want) return null;

    if (have < want) {
      var add = want - have;
      if (ours) return { change: { id: ours.key, quantity: (ours.quantity || 0) + add } };
      return { add: { id: variantId, quantity: add, properties: mark() } };
    }

    // Too much levy in the cart. Only ever wind back OUR OWN line, never anyone else's.
    if (!ours) return null;
    var target = Math.max(0, (ours.quantity || 0) - (have - want));
    return { change: { id: ours.key, quantity: target } };
  }

  function mark() {
    var p = {};
    p[MARK] = 'auto';
    return p;
  }

  var running = false;

  function sync() {
    if (running) return Promise.resolve(false);
    running = true;
    return getCart().then(function (cart) {
      if (!cart || !cart.items) return false;
      // Tags only matter for tires and packs, and not at all for an exempt buyer.
      var handles = [];
      if (!buyerRemitsOwnLevy()) {
        cart.items.forEach(function (l) {
          var ty = l.product_type || '';
          if ((ty === 'Tires' || ty === 'Wheels') && l.handle && !isLevyLine(l) &&
              handles.indexOf(l.handle) === -1) handles.push(l.handle);
        });
      }
      return Promise.all(handles.map(tagsFor)).then(function (got) {
        var tags = {};
        handles.forEach(function (h, i) { tags[h] = got[i]; });
        var steps = ['regular', 'medium']
          .map(function (k) { return planFor(cart, k, tags); })
          .filter(Boolean);
        if (!steps.length) return false;

        // Run them one at a time — each mutation invalidates the line keys of the last.
        return steps.reduce(function (chain, step) {
          return chain.then(function () {
            return step.add ? post('/cart/add.js', { items: [step.add] })
                            : post('/cart/change.js', step.change);
          });
        }, Promise.resolve()).then(function () { return true; });
      });
    }).catch(function () {
      return false;        // a levy we failed to add is caught by the nightly order audit
    }).then(function (changed) {
      running = false;
      return changed;
    });
  }

  function refreshUI(changed) {
    if (!changed) return;
    // Only the cart page shows totals that would now be stale; the drawer re-fetches
    // on its next open, and other pages show nothing but the item count.
    if (/^\/cart($|\?)/.test(window.location.pathname)) window.location.reload();
    else document.dispatchEvent(new CustomEvent('rt:levy-synced'));
  }

  /* Re-sync after anything that mutates the cart. Dawn does all of it through fetch. */
  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var isWrite = CART_WRITE_RE.test(String(url).split('?')[0]);
    var out = nativeFetch.apply(this, arguments);
    if (!isWrite || running) return out;
    return out.then(function (res) {
      // Let Dawn finish rendering its own response before we touch the cart again.
      setTimeout(function () { sync().then(refreshUI); }, 0);
      return res;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sync().then(refreshUI); });
  } else {
    sync().then(refreshUI);
  }
})();
